/* eslint-disable react/prop-types */
import { useAtom } from "jotai";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Layout, Seo, Status } from "~components";
import { getUrl, noOrgFoundRedirect } from "~lib";

import { AccessModal } from "~components/releases";

import {
  competitionSeo,
  CompetitionSeoProps,
} from "~features/competitions/competitionSeo";
import NonSeatedEventReservation from "~features/nonseated-reservation/components/NonSeatedEventReservation";
import { IEventReservationPage } from "~features/reservation/reservation-types";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { Seated as SeatedReservationPage } from "~features/seated-reservation/reservation.Seated";
import * as actions from "~features/seated-reservation/store/actions/index";
import { QueryParams } from "~features/seated-reservation/types";
import { VenueSeating } from "~graphql/sdk";
import { useCampaignReportingOpen } from "~hooks/useBroadastCampaignReporting";
import { useGetEventWithAccessControl } from "~hooks/useGetEventWithAccessControl";
import {
  googleAnalyticsReleaseAtom,
  useGoogleAnalyticsPageView,
} from "~hooks/useGoogleAnalyticsPageView";
import { useReferralCode } from "~hooks/useReferralCode";
import { PageName } from "~telemetry/enums/AttributeNames";
import usePageLoad from "~telemetry/traces/usePageLoad";
import { useOrganization } from "~hooks";
import { useEventTrackingCodes } from "~hooks/useEventTrackingCodes";
import EventHeader from "~components/common/common.EventHeader";
import { getServerSideOrganization, setCacheHeaders } from "~lib/server";
import { OptionalSeekaProvider } from "~lib/seeka";
import { ReleaseType } from "~graphql/typed-document-nodes";
import { HighDemandRedirect } from "~lib/helpers/handleHighDemandRedirect";
import { useFacebookPixelScript } from "~features/tracking/marketing";
import {
  isPrechargeFlowAtom,
  presaleStartDateAtom,
} from "~features/nonseated-reservation/isPrechargeFlowAtom";
import { StripeConfigProvider } from "~lib/stripeConfigContext";
import { MaybeExpressCheckoutProvider } from "~features/checkout/express/components/ExpressCheckout.context";
import { useSeekaConverge } from "@seeka-labs/converge-react";

type Query = {
  eventId?: string;
  release?: string;
  slug?: string;
  ownedTickets?: string;
  session?: string;
  posId?: string;
  customer?: string;
  zone?: string;
};

export const getServerSideProps: GetServerSideProps<
  CompetitionSeoProps
> = async (context) => {
  try {
    const { data, error } = await getServerSideOrganization(context.req);

    if (error) {
      return noOrgFoundRedirect(context.res);
    }

    setCacheHeaders(context.res);

    return await competitionSeo(
      context.req,
      data?.currentOrganization.id,
      context.params?.eventId as string
    );
  } catch (error) {
    if (error?.message === "CURRENT_ORGANIZATION_TIMEOUT") {
      console.error("CURRENT_ORGANIZATION_TIMEOUT");
      return HighDemandRedirect(context.res, context.resolvedUrl);
    }
    return noOrgFoundRedirect(context.res);
  }
};

const Reservation = ({ event, release, isAdmin }: IEventReservationPage) => {
  const router = useRouter();
  const query = router.query as Query;
  const { organization, hasFeature } = useOrganization();

  const converge = useSeekaConverge();

  useEventTrackingCodes();

  // Store referralCode & version in sessionStorage.
  useReferralCode();

  useEffect(() => {
    void router.prefetch("/checkout/[orderId]");
    void router.prefetch("/events/[eventId]");
  }, []);

  useEffect(() => {
    if (router.query?.session !== QueryParams.SessionContinue) {
      actions.resetState();
    }
  }, []);

  const [isPrechargeFlow, setIsPrechargeFlow] = useAtom(isPrechargeFlowAtom);
  const [, setStartDate] = useAtom(presaleStartDateAtom);

  useEffect(() => {
    if (release?.isPrePurchaseActive && !hasFeature("DisablePreauth")) {
      setIsPrechargeFlow(true);
      setStartDate(release.startConditionDate);
    }
  }, [
    release.isPrePurchaseActive,
    hasFeature,
    setIsPrechargeFlow,
    release.startConditionDate,
    setStartDate,
  ]);

  useEffect(() => {
    void converge?.track?.viewProduct({
      productIdentifier: event?.id,
      productName: event?.title,
    });
  }, []);

  if (
    !hasFeature("LegacyGATicketSelection") &&
    event.venue?.seating !== VenueSeating.Seated &&
    (isAdmin || release?.isActive || isPrechargeFlow)
  ) {
    return (
      <>
        <NonSeatedEventReservation
          event={event}
          release={release}
          isAdmin={isAdmin}
        />
      </>
    );
  }

  return (
    <>
      <EventHeader
        type="event"
        venue={event?.venue}
        title={event.title}
        startDate={event.startDate}
        endDate={event.endDate}
        goBack={() => {
          if (query.posId) {
            window.location.href = `${getUrl(organization.slug)}/pos/${
              query.posId
            }`;
          } else {
            void router.push("/");
          }
        }}
        accessibility={event.accessibility}
        supportInfo={organization?.supportInfo}
        image={event.logo}
        description={event.description}
        coordinates={`${event.venue?.location?.lat},${event.venue?.location?.lng}`}
      />
      <Layout layoutType="sub">
        {(isAdmin || release?.isActive) && (
          <>
            {event.venue?.seating !== VenueSeating.Seated ? (
              <NonSeatedEventReservation
                event={event}
                release={release}
                isAdmin={isAdmin}
              />
            ) : (
              <SeatedReservationPage
                type="event"
                typeId={query.eventId}
                release={release}
                defaultShowOptions={!query.ownedTickets && !query.session}
              />
            )}
            <TicketDetailsModal />
          </>
        )}
      </Layout>
    </>
  );
};

const ReservationPage: NextPage<CompetitionSeoProps> = ({
  activeCompetition: { data: activeCompetition } = {},
  eventImageUrls: { data: eventImageUrls } = {},
  eventTitle: { data: eventTitle } = {},
}) => {
  const router = useRouter();
  const { hasFeature } = useOrganization();

  const { eventId } = router.query as Query;
  useGoogleAnalyticsPageView(
    !!eventId,
    PageName.EVENT_RESERVATION_PAGE,
    eventId
  );
  usePageLoad(
    PageName.EVENT_RESERVATION_PAGE,
    {
      forwardToNest: true,
      eventId: eventId,
    },
    // Only fire the page-load when:
    // - the event is loaded
    !!eventId
  );

  const { error, event, release, isAdmin, isLoading, access } =
    useGetEventWithAccessControl();

  const [hasAccess, setHasAccess] = useState<boolean>(false);

  useFacebookPixelScript(event?.pixelId);

  const [, setRelease] = useAtom(googleAnalyticsReleaseAtom);

  useEffect(() => {
    if (release) {
      //  @ts-expect-error atom type issue while using strict:false
      setRelease(release);
    }
  }, [release]);

  useCampaignReportingOpen(!!event);

  useEffect(() => {
    if (release) {
      setHasAccess(!release?.requiresAuth);
    }
  }, [release]);

  const defaultTitle = `Buy Tickets${eventTitle ? ` - ${eventTitle}` : ""}`;

  useEffect(() => {
    if (!isLoading && !access.ticketPage) {
      if (access.infoPage) {
        void router.replace({
          pathname: `/events/[eventId]`,
          query: {
            eventId,
          },
        });
      } else {
        void router.replace("/");
      }
    }
  }, [isLoading, access.ticketPage, access.infoPage, eventId, router]);

  return (
    <>
      <Seo
        title={activeCompetition ? activeCompetition.title : defaultTitle}
        description={undefined} // fallback to seo metadata description on event
        options={{
          openGraph: {
            title: activeCompetition ? activeCompetition.title : defaultTitle,
          },
        }}
      />

      <Status
        error={error}
        loading={isLoading || !event?.venue?.seating || !access.ticketPage}
      >
        {hasAccess || isAdmin ? (
          <OptionalSeekaProvider
            seekaConfig={event?.seekaConfig}
            enabled={release?.type !== ReleaseType.Pos}
          >
            <StripeConfigProvider config={event?.stripeConfig}>
              <MaybeExpressCheckoutProvider>
                <Reservation
                  event={event}
                  release={release}
                  isAdmin={isAdmin}
                />
              </MaybeExpressCheckoutProvider>
            </StripeConfigProvider>
          </OptionalSeekaProvider>
        ) : (
          <AccessModal setHasAccess={setHasAccess} release={release} />
        )}
      </Status>
    </>
  );
};

export default ReservationPage;
